import { inject, Pipe, PipeTransform } from '@angular/core';
import { SelectedLanguageLocaleService } from '../util/selected-language-locale.service';

export type LocalizedDateStyle = 'longDate' | 'shortDate' | 'shortTime' | 'short';

const FORMAT_OPTIONS: Record<LocalizedDateStyle, Intl.DateTimeFormatOptions> = {
    longDate: { dateStyle: 'long' },
    shortDate: { dateStyle: 'short' },
    shortTime: { timeStyle: 'short' },
    short: { dateStyle: 'short', timeStyle: 'short' }
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(locale: string, style: LocalizedDateStyle): Intl.DateTimeFormat {
    const key = `${locale}|${style}`;
    let formatter = formatterCache.get(key);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, FORMAT_OPTIONS[style]);
        formatterCache.set(key, formatter);
    }
    return formatter;
}

/**
 * Formats a date using the browser's `Intl.DateTimeFormat` for the given locale. Unlike Angular's
 * `DatePipe`/`formatDate`, no locale data needs to be registered, so any locale supported by the
 * runtime works. Use this helper wherever a date must be formatted outside of a template.
 */
export function formatLocalizedDate(
    value: string | number | Date | null | undefined,
    style: LocalizedDateStyle = 'longDate',
    locale = 'en-US'
): string {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    return getFormatter(locale, style).format(date);
}

/**
 * Formats a date for the currently selected application language using `Intl.DateTimeFormat`.
 * The locale is resolved internally from {@link SelectedLanguageLocaleService} (defaulting to
 * `en-US`), so templates only need to provide the value and an optional style. An explicit locale
 * can still be passed as the third argument to override the selected language.
 *
 * The pipe is impure so it reacts to runtime language switches; formatting is memoized to keep it
 * cheap even when used in large tables.
 */
@Pipe({ name: 'localizedDate', pure: false })
export class LocalizedDatePipe implements PipeTransform {
    private readonly selectedLocale = inject(SelectedLanguageLocaleService).getLocale('en-US');

    private lastValue?: string | number | Date | null;
    private lastStyle?: LocalizedDateStyle;
    private lastLocale?: string;
    private lastResult = '';

    public transform(
        value: string | number | Date | null | undefined,
        style: LocalizedDateStyle = 'longDate',
        locale?: string
    ): string {
        const resolvedLocale = locale ?? this.selectedLocale();
        if (value === this.lastValue && style === this.lastStyle && resolvedLocale === this.lastLocale) {
            return this.lastResult;
        }
        this.lastValue = value;
        this.lastStyle = style;
        this.lastLocale = resolvedLocale;
        this.lastResult = formatLocalizedDate(value, style, resolvedLocale);
        return this.lastResult;
    }
}
